/**
 * @file src/services/api.js
 * @description Central API helper for all client -> server requests.
 *
 * The goal is to keep fetch logic consistent (base URL, JSON parsing,
 * auth headers, and predictable errors) so pages/components stay clean.
 */

const getApiBaseUrl = () => {
  // You can override this in public/index.html (window.__CONFIG__).
  if (window.__CONFIG__ && window.__CONFIG__.API_BASE_URL) {
        const v = window.__CONFIG__.API_BASE_URL;
    if (!v) return "";
    return v.replace(/\/$/, "");
  }

  // Default local dev: Flask on 5000.
    // If deployed (non-localhost), use same-origin relative API.
  const host = window.location.hostname;
  if (host && host !== "localhost" && host !== "127.0.0.1") return "";

  return "http://localhost:5000";
};

const getAuthToken = () => localStorage.getItem("token");

const jsonHeaders = (includeAuth = false) => {
  const headers = { "Content-Type": "application/json" };
  if (includeAuth) {
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Small wrapper around fetch that:
 * - prefixes API base URL
 * - parses JSON safely
 * - throws a readable error message on non-2xx responses
 */
export async function apiFetch(path, options = {}) {
  const base = getApiBaseUrl();
  const url = `${base}${path.startsWith("/") ? "" : "/"}${path}`;

  const res = await fetch(url, options);

  // Some errors return HTML (proxies). Try JSON first, fallback to text.
  const contentType = res.headers.get("content-type") || "";
  let body;
  if (contentType.includes("application/json")) {
    body = await res.json();
  } else {
    const text = await res.text();
    try {
      body = JSON.parse(text);
    } catch {
      body = { message: text };
    }
  }

  if (!res.ok) {
    const msg = body?.message || `Request failed (${res.status})`;
    const error = new Error(msg);
    error.status = res.status;
    error.body = body;
    throw error;
  }

  return body;
}

// Convenience wrappers
export const AuthAPI = {
  register: (payload) => apiFetch("/api/register", { method: "POST", headers: jsonHeaders(false), body: JSON.stringify(payload) }),
  login: (payload) => apiFetch("/api/login", { method: "POST", headers: jsonHeaders(false), body: JSON.stringify(payload) }),
  me: () => apiFetch("/api/me", { method: "GET", headers: jsonHeaders(true) }),
};

export const BooksAPI = {
  list: ({ q = "", page = 1, per_page = 12 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    params.set("page", String(page));
    params.set("per_page", String(per_page));
    return apiFetch(`/api/books?${params.toString()}`, { method: "GET" });
  },
  create: (payload) => apiFetch("/api/books", { method: "POST", headers: jsonHeaders(true), body: JSON.stringify(payload) }),
  update: (id, payload) => apiFetch(`/api/books/${id}`, { method: "PUT", headers: jsonHeaders(true), body: JSON.stringify(payload) }),
  remove: (id) => apiFetch(`/api/books/${id}`, { method: "DELETE", headers: jsonHeaders(true) }),
  addReview: (id, payload) => apiFetch(`/api/books/${id}/reviews`, { method: "POST", headers: jsonHeaders(true), body: JSON.stringify(payload) }),
};

export const AuthStore = {
  isLoggedIn: () => Boolean(getAuthToken()),
  setSession: (token, user) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user_id", String(user?.id || ""));
    localStorage.setItem("username", user?.username || "");
  },
  clearSession: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("username");
  },
};
